import { createFileRoute, useSearch, useNavigate, Link } from '@tanstack/react-router'
import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { mdiPlus, mdiRefresh, mdiMagnify, mdiCheck, mdiClose, mdiPencil } from '@mdi/js'
import { AppIcon } from '../../components/ui/icon'
import { Button } from '../../components/ui/button'
import { Input } from '../../components/ui/input'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table'
import { CreateOrganizationDialog } from '../../components/CreateOrganizationDialog'
import { OrganizationStatusBadge } from '../../components/admin/OrganizationStatusBadge'
import { OrganizationActionDialog, type OrganizationAction } from '../../components/admin/OrganizationActionDialog'
import { OrganizationEditDrawer } from '../../components/admin/OrganizationEditDrawer'
import { useBreadcrumbs } from '../../hooks/useBreadcrumbs'
import { useAuth, useIsPlatformAdmin } from '../../hooks/useAuth'
import {
  useOrganizationListUnified,
  useSuspendAdminOrganization,
  useUnsuspendAdminOrganization,
  useDeleteAdminOrganization,
  useRestoreAdminOrganization,
  type NormalizedOrg,
  type OrgStatus,
} from '../../hooks/useQueries'
import { PageHeader } from '../../components/shared/PageHeader'
import { SECTION_ICONS } from '../../lib/section-icons'
import { useMyOrganizations } from '../../api/multiOrg'
import { EmptyState } from '../../components/ui/empty-state'

type StatusFilter = OrgStatus | 'all'
const STATUS_VALUES: StatusFilter[] = ['active', 'suspended', 'deleted', 'template', 'all']

interface OrganizationsSearch {
  q?: string
  status?: StatusFilter
  page?: number
  limit?: number
  org?: string
}

export const Route = createFileRoute('/organizations/')({
  validateSearch: (search: Record<string, unknown>): OrganizationsSearch => {
    const out: OrganizationsSearch = {}
    if (typeof search.q === 'string') out.q = search.q
    if (typeof search.status === 'string' && (STATUS_VALUES as string[]).includes(search.status)) {
      out.status = search.status as StatusFilter
    }
    if (typeof search.page === 'number') out.page = search.page
    if (typeof search.limit === 'number') out.limit = search.limit
    if (typeof search.org === 'string') out.org = search.org
    return out
  },
  component: RouteComponent,
})

function RouteComponent() {
  const { t, i18n } = useTranslation('admin')
  const { t: tGroup } = useTranslation('group-steering')
  const search = useSearch({ from: '/organizations/' })
  const navigate = useNavigate({ from: '/organizations/' })
  const isAdmin = useIsPlatformAdmin()
  const { orgCode } = useAuth()
  const [createOpen, setCreateOpen] = useState(false)
  // The group console entry, for whoever holds group_steering.read in that
  // organization (spec 2026-09-11-01): the selector, the home page and the
  // main menu are its doors.
  const { data: myOrgs } = useMyOrganizations()
  const canGroupSteering = (code: string) => (myOrgs?.data ?? []).some((o) => o.code === code && (o.permissions ?? []).includes('group_steering.read'))
  const [pendingAction, setPendingAction] = useState<{ org: NormalizedOrg; action: OrganizationAction } | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)

  // This route is deliberately org-less (a cross-org, server-wide surface),
  // so there is no `$org` route param to build the "Home" / "Settings"
  // crumb links from. Fall back to the signed-in user's org, same as
  // AppSidebar.tsx and CommandPalette.tsx do for other org-less contexts.
  const breadcrumbOrg = orgCode || 'demo'
  useBreadcrumbs([
    { title: t('common:nav.home'), path: `/${breadcrumbOrg}` },
    { title: t('settings:page.title'), path: `/${breadcrumbOrg}/settings` },
    { title: t('organizations.title') },
  ])

  const filters = useMemo(() => ({
    q: search.q || undefined,
    status: isAdmin ? ((search.status || 'active') as StatusFilter) : undefined,
    page: search.page || 1,
    limit: search.limit || 20,
  }), [search.q, search.status, search.page, search.limit, isAdmin])

  const { rows, isLoading, error, refetch } = useOrganizationListUnified(filters, isAdmin)

  const suspend = useSuspendAdminOrganization()
  const unsuspend = useUnsuspendAdminOrganization()
  const del = useDeleteAdminOrganization()
  const restore = useRestoreAdminOrganization()

  const updateSearch = (patch: Partial<OrganizationsSearch>, options?: { replace?: boolean }) => {
    navigate({ search: (prev: Record<string, unknown>) => ({ ...prev, ...patch }) as any, replace: options?.replace })
  }

  const handleRowClick = (org: NormalizedOrg) => {
    navigate({ to: '/$org', params: { org: org.code } })
  }

  const handleEditClick = (e: React.MouseEvent, org: NormalizedOrg) => {
    e.stopPropagation()
    updateSearch({ org: org.code })
  }

  const executeAction = async () => {
    if (!pendingAction) return
    const { org, action } = pendingAction
    const params = { path: { orgUid: org.uid } }
    try {
      if (action === 'suspend') await suspend.mutateAsync({ params })
      else if (action === 'unsuspend') await unsuspend.mutateAsync({ params })
      else if (action === 'delete') await del.mutateAsync({ params })
      else if (action === 'restore') await restore.mutateAsync({ params })
      setPendingAction(null)
      setActionError(null)
    } catch (err: any) {
      setActionError(err?.title || err?.message || 'Action failed')
    }
  }

  const formatDate = (iso?: string | null) => {
    if (!iso) return '—'
    try { return new Date(iso).toLocaleDateString(i18n.language) } catch { return iso }
  }

  const selectedOrg = rows.find((o) => o.code === search.org)

  return (
    <div className="space-y-6">
      <PageHeader
        icon={SECTION_ICONS.organizations}
        title={t('organizations.title')}
        actions={
        <>
          {isAdmin && (
          <Button
            onClick={() => setCreateOpen(true)}
            className="bg-primary hover:bg-primary/90"
            data-testid="create-organization-button"
            aria-label={t('organizations.action.create')}
            title={t('organizations.action.create')}
          >
            <AppIcon path={mdiPlus} size="md" className="md:mr-1" />
            <span className="hidden md:inline">{t('organizations.action.create')}</span>
          </Button>
          )}
          <Button
            onClick={() => refetch()}
            variant="outline"
            disabled={isLoading}
            data-testid="refresh-button"
            aria-label={t('organizations.action.refresh')}
            title={t('organizations.action.refresh')}
          >
            <AppIcon path={mdiRefresh} size="md" className="md:mr-1" />
            <span className="hidden md:inline">{t('organizations.action.refresh')}</span>
          </Button>
        </>
        }
      />

      <div className="bg-card rounded-lg border border-border p-4">
        <div className="flex gap-4 items-center flex-wrap">
          <div className="flex items-center border border-border rounded-md px-3 py-2 bg-background min-w-64">
            <AppIcon tone="muted" path={mdiMagnify} size="sm" className="mr-2" />
            <Input
              type="text"
              placeholder={t('organizations.search_placeholder')}
              value={search.q ?? ''}
              onChange={(e) => updateSearch({ q: e.target.value, page: 1 }, { replace: true })}
              className="bg-transparent border-none outline-none text-sm flex-1 text-foreground placeholder:text-muted-foreground p-0 h-auto shadow-none focus-visible:ring-0"
              data-testid="search-input"
            />
          </div>

          {isAdmin && (
            <div className="inline-flex rounded-md border p-1" role="tablist" data-testid="status-filter">
              {STATUS_VALUES.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => updateSearch({ status: s, page: 1 })}
                  className={`px-3 py-1 text-sm rounded ${(filters.status || 'active') === s ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}
                  data-testid={`status-filter-${s}`}
                >
                  {t(`organizations.status.${s}`)}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {actionError && (
        <div className="text-sm text-destructive" data-testid="action-error">
          {t('organizations.action_failed', { error: actionError })}
        </div>
      )}

      {error && (
        <div className="bg-destructive/10 border border-destructive/20 rounded-md p-4">
          <div className="text-destructive text-sm">
            {(error as any)?.title || (error as any)?.message || 'An error occurred'}
          </div>
        </div>
      )}

      <div className="bg-card rounded-lg border border-border overflow-hidden">
        {isLoading ? (
          <div className="px-6 py-4 text-center text-muted-foreground">{t('organizations.loading')}</div>
        ) : rows.length === 0 ? (
          <EmptyState
            variant="inline"
            live
            className="py-8"
            title={t(search.q || (search.status && search.status !== 'active') ? 'organizations.empty.filtered' : 'organizations.empty.none')}
            action={(search.q || search.status) && (
              <Button variant="outline" size="sm" onClick={() => navigate({ search: {} })} data-testid="clear-filters">
                {t('organizations.empty.clear')}
              </Button>
            )}
          />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('organizations.column.name')}</TableHead>
                <TableHead>{t('organizations.column.code')}</TableHead>
                <TableHead>{t('organizations.column.template')}</TableHead>
                {isAdmin && <TableHead>{t('organizations.column.status')}</TableHead>}
                {isAdmin && <TableHead className="text-right">{t('organizations.column.members')}</TableHead>}
                {isAdmin && <TableHead className="text-right">{t('organizations.column.assets')}</TableHead>}
                <TableHead>{t('organizations.column.created')}</TableHead>
                {isAdmin && <TableHead>{t('organizations.column.last_active')}</TableHead>}
                <TableHead className="text-right">{t('organizations.column.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((org) => (
                <TableRow
                  key={org.uid}
                  data-testid={`org-row-${org.code}`}
                  className="cursor-pointer"
                  onClick={() => handleRowClick(org)}
                >
                  <TableCell className="text-foreground">{org.name || '—'}</TableCell>
                  <TableCell className="font-mono text-muted-foreground">{org.code}</TableCell>
                  <TableCell>
                    {org.is_template ? (
                      <AppIcon path={mdiCheck} size="md" className="text-green-600" />
                    ) : (
                      <AppIcon tone="muted" path={mdiClose} size="md" />
                    )}
                  </TableCell>
                  {isAdmin && (
                    <TableCell>{org.status && <OrganizationStatusBadge status={org.status} />}</TableCell>
                  )}
                  {isAdmin && <TableCell className="text-right">{org.counts?.members ?? 0}</TableCell>}
                  {isAdmin && <TableCell className="text-right">{org.counts?.assets ?? 0}</TableCell>}
                  <TableCell className="text-muted-foreground">{formatDate(org.created_at)}</TableCell>
                  {isAdmin && <TableCell className="text-muted-foreground">{formatDate(org.last_active_at)}</TableCell>}
                  <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                    {canGroupSteering(org.code) && (
                      <Button variant="ghost" size="icon" asChild className="mr-1" data-testid={`org-group-steering-${org.code}`}>
                        <Link
                          to="/$org/group"
                          params={{ org: org.code }}
                          search={{ period: undefined, level: undefined, family: undefined, kpi: undefined, selected: undefined, q: undefined }}
                          aria-label={tGroup('space.title')}
                          title={tGroup('space.title')}
                        >
                          <AppIcon path={SECTION_ICONS.group_steering} size="sm" />
                        </Link>
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="icon"
                      aria-label={t('organizations.action.edit')}
                      title={t('organizations.action.edit')}
                      data-testid={`org-edit-${org.code}`}
                      onClick={(e) => handleEditClick(e, org)}
                    >
                      <AppIcon path={mdiPencil} size="sm" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {rows.length > 0 && (
        <div className="text-sm text-muted-foreground">
          {t('organizations.count', { count: rows.length, defaultValue: `Showing ${rows.length} organization${rows.length !== 1 ? 's' : ''}` })}
        </div>
      )}

      <OrganizationEditDrawer
        orgCode={search.org}
        isAdmin={isAdmin}
        onClose={() => updateSearch({ org: undefined })}
        onAction={(action) => {
          if (selectedOrg) setPendingAction({ org: selectedOrg, action })
        }}
      />

      <OrganizationActionDialog
        action={pendingAction?.action ?? null}
        orgCode={pendingAction?.org.code ?? ''}
        onCancel={() => setPendingAction(null)}
        onConfirm={executeAction}
        pending={suspend.isPending || unsuspend.isPending || del.isPending || restore.isPending}
      />

      <CreateOrganizationDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onSuccess={(created) => {
          setCreateOpen(false)
          // An organization created from a template starts empty: lead the
          // admin to its first sync (spec 2026-09-25-02). A hard navigation,
          // so the new organization gets its own auth context.
          if (created.fromTemplate) {
            window.location.assign(`/${created.code}/types/sync`)
            return
          }
          refetch()
        }}
      />
    </div>
  )
}
